import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Mail, User, Building, Phone, CreditCard, CheckCircle, AlertCircle, Smartphone, RefreshCw, Info, Copy, X, RotateCcw } from 'lucide-react';
import Logo from './Logo';

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 90000;
const STORAGE_KEY = 'tuneup_analysis_form';

function normalizePhone(input) {
  const digits = input.replace(/\D/g, '');
  if (digits.startsWith('254')) return digits;
  if (digits.startsWith('0')) return `254${digits.slice(1)}`;
  if (digits.startsWith('7') || digits.startsWith('1')) return `254${digits}`;
  return digits;
}

const EMPTY_FORM = { name: '', email: '', website: '', company: '', phone: '', projectType: 'Website Analysis' };

function loadDraft() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveDraft(formData) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
  } catch {}
}

function clearDraft() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
}

const AnalysisForm = ({ onClose, onSuccess }) => {
  const draft = loadDraft();
  const [formData, setFormData] = useState(draft || EMPTY_FORM);
  const [hasDraft] = useState(!!draft && Object.values(draft).some((v) => v && v !== 'Website Analysis'));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  // step: 'form' | 'waiting' | 'failed' | 'timeout' | 'manual'
  const [step, setStep] = useState('form');
  const [projectId, setProjectId] = useState(null);
  const [failureReason, setFailureReason] = useState('');
  const [manualPayment, setManualPayment] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [mpesaCodeInput, setMpesaCodeInput] = useState('');
  const [isSubmittingCode, setIsSubmittingCode] = useState(false);
  const [codeSubmitted, setCodeSubmitted] = useState(false);
  const [codeSubmitError, setCodeSubmitError] = useState('');
  const pollTimer = useRef(null);
  const pollDeadline = useRef(null);

  useEffect(() => {
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, []);

  // Persist form data on every change (form step only)
  useEffect(() => {
    if (step === 'form') saveDraft(formData);
  }, [formData, step]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const pollStatus = async (id) => {
    try {
      const response = await fetch(`/api/analysis-request/${id}/status`);
      const data = await response.json();

      if (data.status === 'Paid') {
        clearDraft();
        onSuccess({ projectId: id });
        return;
      }

      if (data.failureReason) {
        setFailureReason(data.failureReason);
        setStep('failed');
        return;
      }

      if (Date.now() >= pollDeadline.current) {
        setStep('timeout');
        return;
      }

      pollTimer.current = setTimeout(() => pollStatus(id), POLL_INTERVAL_MS);
    } catch (err) {
      if (Date.now() >= pollDeadline.current) {
        setStep('timeout');
        return;
      }
      pollTimer.current = setTimeout(() => pollStatus(id), POLL_INTERVAL_MS);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/analysis-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, phone: normalizePhone(formData.phone) }),
      });

      const data = await response.json();

      if (response.ok) {
        setProjectId(data.projectId);
        if (data.manualPayment) {
          setManualPayment(data.manualPayment);
          setStep('manual');
        } else {
          setStep('waiting');
          pollDeadline.current = Date.now() + POLL_TIMEOUT_MS;
          pollStatus(data.projectId);
        }
      } else {
        setError(data.error || 'Failed to submit analysis request');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const retry = () => {
    setFailureReason('');
    setStep('form');
  };

  const hasFormData = Object.values(formData).some((v) => v && v !== 'Website Analysis');

  const requestClose = () => {
    if (step === 'waiting' || step === 'manual') return; // payment in progress — block
    if (step === 'form' && hasFormData) {
      setShowConfirm(true);
    } else {
      onClose();
    }
  };

  const confirmDiscard = () => {
    clearDraft();
    onClose();
  };

  const submitMpesaCode = async (e) => {
    e.preventDefault();
    setIsSubmittingCode(true);
    setCodeSubmitError('');

    try {
      const response = await fetch(`/api/analysis-request/${projectId}/mpesa-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mpesaCode: mpesaCodeInput }),
      });

      if (response.ok) {
        setCodeSubmitted(true);
        clearDraft();
      } else {
        const data = await response.json();
        setCodeSubmitError(data.error || 'Failed to submit code. Please try again.');
      }
    } catch (err) {
      setCodeSubmitError('Network error. Please try again.');
    } finally {
      setIsSubmittingCode(false);
    }
  };

  return createPortal(
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {(step === 'form' || step === 'failed' || step === 'timeout') && (
        <button
          type="button"
          onClick={requestClose}
          aria-label="Close"
          className="fixed top-4 right-4 z-[60] text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      )}
      <motion.div
        className="relative bg-slate-900 border border-white/10 rounded-2xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
      >
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <Logo size="large" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">Website Analysis</h3>
          <p className="text-gray-400 text-sm">
            Get comprehensive website analysis for KES 3,450
          </p>
        </div>

        {step === 'form' && (
          <>
            {hasDraft && (
              <div className="bg-orange-600/10 border border-orange-500/30 rounded-lg p-3 flex items-center gap-3 mb-4">
                <RotateCcw className="w-4 h-4 text-orange-400 shrink-0" />
                <span className="text-orange-200 text-sm flex-1">We saved your details. Picking up where you left off.</span>
              </div>
            )}

            {/* Price Display */}
            <div className="bg-gradient-to-br from-red-600/30 to-orange-600/30 ring-1 ring-inset ring-white/15 border border-red-500/30 rounded-lg p-4 mb-6 text-center shadow-inner">
              <div className="text-white font-bold text-xl">KES 3,450</div>
              <div className="text-gray-400 text-xs">One-time comprehensive analysis</div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  <User className="w-4 h-4 inline mr-1" />
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 transition-colors"
                  placeholder="e.g., Grace Wanjiru"
                />
              </div>

              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 transition-colors"
                  placeholder="e.g., grace@company.co.ke"
                />
              </div>

              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  <Phone className="w-4 h-4 inline mr-1" />
                  M-Pesa Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  pattern="^(0|\+?254)?[71]\d{8}$"
                  title="Enter a valid Kenyan phone number, e.g. 0712345678"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 transition-colors"
                  placeholder="e.g., 0712345678"
                />
                <p className="text-gray-500 text-xs mt-1">You'll get an M-Pesa prompt on this number to complete payment.</p>
              </div>

              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  <Globe className="w-4 h-4 inline mr-1" />
                  Website URL *
                </label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 transition-colors"
                  placeholder="e.g., https://mybusiness.co.ke"
                />
              </div>

              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  <Building className="w-4 h-4 inline mr-1" />
                  Company Name
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 transition-colors"
                  placeholder="e.g., Nairobi Tech Ltd (Optional)"
                />
              </div>

              {error && (
                <div className="bg-red-600/20 border border-red-500/30 rounded-lg p-3 flex items-center">
                  <AlertCircle className="w-4 h-4 text-red-400 mr-2" />
                  <span className="text-red-400 text-sm">{error}</span>
                </div>
              )}

              <motion.button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center"
                whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Sending M-Pesa prompt...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Pay with M-Pesa
                  </>
                )}
              </motion.button>
            </form>

            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="flex items-start">
                <CheckCircle className="w-4 h-4 text-orange-400 mr-2 mt-0.5 flex-shrink-0" />
                <div className="text-gray-400 text-xs">
                  <p className="mb-2">What happens next:</p>
                  <ul className="space-y-1 ml-4">
                    <li>• An M-Pesa prompt appears on your phone instantly</li>
                    <li>• Enter your PIN to confirm the KES 3,450 payment</li>
                    <li>• Analysis report within 24-48 hours of payment</li>
                  </ul>
                </div>
              </div>
            </div>

            <button
              onClick={requestClose}
              className="mt-4 w-full text-center text-gray-400 hover:text-white text-sm transition-colors"
            >
              Cancel
            </button>
          </>
        )}

        {step === 'waiting' && (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-gradient-to-r from-red-600 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Smartphone className="w-8 h-8 text-white" />
            </div>
            <h4 className="text-white font-semibold text-lg mb-2">Check your phone</h4>
            <p className="text-gray-400 text-sm mb-6">
              We've sent an M-Pesa prompt to <span className="text-white">{formData.phone}</span>. Enter your PIN to complete the KES 3,450 payment.
            </p>
            <div className="w-8 h-8 border-2 border-white/30 border-t-orange-400 rounded-full animate-spin mx-auto" />
          </div>
        )}

        {step === 'manual' && manualPayment && (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-gradient-to-r from-red-600 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-8 h-8 text-white" />
            </div>
            <h4 className="text-white font-semibold text-lg mb-2">Pay via M-Pesa Paybill</h4>
            <p className="text-gray-400 text-sm mb-4">
              We're still finalizing automated M-Pesa checkout, but our Paybill is open and ready to receive payments
              right now. Pay manually below, then come back here and submit your M-Pesa code so we can confirm it.
            </p>

            <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4 text-left space-y-3">
              <div>
                <div className="text-gray-500 text-xs uppercase tracking-wide">Business Number (Paybill)</div>
                <div className="text-white font-bold text-xl">{manualPayment.paybillNumber}</div>
              </div>
              <div>
                <div className="text-gray-500 text-xs uppercase tracking-wide">Amount</div>
                <div className="text-white font-bold text-xl">KES {manualPayment.amount.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-gray-500 text-xs uppercase tracking-wide">Account Number (use this exactly)</div>
                <div className="flex items-center justify-between">
                  <div className="text-orange-400 font-bold text-xl tracking-wide">{manualPayment.reference}</div>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(manualPayment.reference);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    className="text-gray-400 hover:text-white p-1"
                    aria-label="Copy reference"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                {copied && <div className="text-green-400 text-xs mt-1">Copied!</div>}
              </div>
            </div>

            <div className="bg-orange-600/10 border border-orange-500/30 rounded-lg p-3 flex items-start text-left mb-6">
              <Info className="w-4 h-4 text-orange-400 mr-2 mt-0.5 flex-shrink-0" />
              <p className="text-orange-200 text-xs">
                Go to M-Pesa &gt; Lipa na M-Pesa &gt; Paybill, enter the business number and the account number above.
                After paying, enter the M-Pesa transaction code from your confirmation SMS below and we'll verify it
                against our Paybill statement.
              </p>
            </div>

            {!codeSubmitted ? (
              <form onSubmit={submitMpesaCode} className="text-left mb-4">
                <label className="block text-white text-sm font-medium mb-2">M-Pesa Transaction Code</label>
                <input
                  type="text"
                  value={mpesaCodeInput}
                  onChange={(e) => setMpesaCodeInput(e.target.value)}
                  required
                  placeholder="e.g., QGH7XXXXX1"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors mb-3"
                />
                {codeSubmitError && (
                  <div className="bg-red-600/20 border border-red-500/30 rounded-lg p-3 flex items-center mb-3">
                    <AlertCircle className="w-4 h-4 text-red-400 mr-2 flex-shrink-0" />
                    <span className="text-red-400 text-sm">{codeSubmitError}</span>
                  </div>
                )}
                <motion.button
                  type="submit"
                  disabled={isSubmittingCode}
                  className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300"
                  whileHover={{ scale: isSubmittingCode ? 1 : 1.02 }}
                  whileTap={{ scale: isSubmittingCode ? 1 : 0.98 }}
                >
                  {isSubmittingCode ? 'Submitting...' : 'Submit M-Pesa Code'}
                </motion.button>
              </form>
            ) : (
              <div className="bg-green-600/10 border border-green-500/30 rounded-lg p-4 mb-4 flex items-center">
                <CheckCircle className="w-5 h-5 text-green-400 mr-2 flex-shrink-0" />
                <span className="text-green-200 text-sm">Thanks - we'll verify this shortly and email you once confirmed.</span>
              </div>
            )}

            <button
              onClick={onClose}
              className="w-full text-center text-gray-400 hover:text-white text-sm transition-colors"
            >
              {codeSubmitted ? 'Close' : "I'll come back to this"}
            </button>
          </div>
        )}

        {step === 'failed' && (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-red-600/20 border border-red-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h4 className="text-white font-semibold text-lg mb-2">Payment didn't go through</h4>
            <p className="text-gray-400 text-sm mb-6">{failureReason || 'The M-Pesa payment was not completed.'}</p>
            <button
              onClick={retry}
              className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </button>
          </div>
        )}

        {step === 'timeout' && (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-orange-600/20 border border-orange-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-orange-400" />
            </div>
            <h4 className="text-white font-semibold text-lg mb-2">Still waiting on that?</h4>
            <p className="text-gray-400 text-sm mb-6">
              We haven't heard back from M-Pesa yet. If you already paid, hang tight - we'll follow up by email. Otherwise, you can try again.
            </p>
            <button
              onClick={retry}
              className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </button>
          </div>
        )}

        {/* Discard confirmation overlay */}
        <AnimatePresence>
          {showConfirm && (
            <motion.div
              className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-slate-900/95 backdrop-blur-sm p-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="text-center">
                <div className="w-12 h-12 bg-orange-600/20 border border-orange-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-6 h-6 text-orange-400" />
                </div>
                <h4 className="text-white font-bold text-lg mb-2">Cancel your request?</h4>
                <p className="text-gray-400 text-sm mb-6">Your details are saved — you can reopen this form anytime and continue from here. Or discard everything and start fresh.</p>
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => setShowConfirm(false)}
                    className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white px-6 py-3 rounded-lg font-semibold transition-all"
                  >
                    Keep going
                  </button>
                  <button
                    onClick={onClose}
                    className="w-full border border-gray-700 hover:border-gray-500 text-gray-300 hover:text-white text-sm font-medium transition-colors py-2.5 rounded-lg"
                  >
                    Save & close — continue later
                  </button>
                  <button
                    onClick={confirmDiscard}
                    className="w-full text-red-400 hover:text-red-300 text-sm transition-colors py-2"
                  >
                    Discard everything and close
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default AnalysisForm;
